import { Component } from '@theme/component';

const DEFAULT_CENTER = [8.5347, 52.0211]; // [lng, lat] — geographic center of Germany
const DEFAULT_ZOOM = 8.2;
const DETAIL_ZOOM = 14;
const MAX_SUGGESTIONS = 6;

/**
 * @typedef {{ name: string, address: string, lat: number, lng: number }} Dealer
 *
 * @typedef {object} DealerMapRefs
 * @property {HTMLInputElement} searchInput
 * @property {HTMLUListElement} suggestionsList
 * @property {HTMLElement} mapCanvas
 *
 * @extends {Component<DealerMapRefs>}
 */
export class DealerMapComponent extends Component {
  requiredRefs = ['searchInput', 'suggestionsList', 'mapCanvas'];

  /** @type {Dealer[]} */
  #dealers = [];
  /** @type {import('maplibre-gl').Map | null} */
  #map = null;
  /** @type {Map<Dealer, import('maplibre-gl').Marker>} */
  #markersByDealer = new Map();
  /** @type {Dealer[]} */
  #activeSuggestions = [];
  #activeSuggestionIndex = -1;

  connectedCallback() {
    super.connectedCallback();

    try {
      this.#map = new maplibregl.Map({
        container: this.refs.mapCanvas,
        style: 'https://tiles.openfreemap.org/styles/liberty',
        center: DEFAULT_CENTER,
        zoom: DEFAULT_ZOOM,
      });
      this.#map.on('load', () => this.#localizeLabels());
    } catch (error) {
      // e.g. WebGL unavailable (GPU driver issue, browser/OS restriction).
      // Search must keep working without a map, so this is caught here
      // rather than left to crash connectedCallback — but only the
      // map/marker setup is skipped; dealer data loading below still runs.
      console.error('[dealer-map] failed to initialize MapLibre:', error);
    }

    this.#loadDealers();
  }

  /**
   * Dealer data ships as a static asset (assets/dealers.json, generated by
   * elegante-ops' fachhandel-geocode job) rather than a live Liquid
   * metaobjects query — see sections/haendlersuche-map.liquid for why.
   */
  async #loadDealers() {
    const url = this.dataset.dealersUrl;
    if (!url) return;

    try {
      const response = await fetch(url);
      if (!response.ok) throw new Error(`request failed: ${response.status}`);

      const parsed = await response.json();
      this.#dealers = Array.isArray(parsed) ? parsed : [];
    } catch (error) {
      console.error('[dealer-map] failed to load dealer data:', error);
      return;
    }

    if (!this.#map) return;

    for (const dealer of this.#dealers) {
      const marker = new maplibregl.Marker({ element: this.#createMarkerElement() })
        .setLngLat([dealer.lng, dealer.lat])
        .setPopup(new maplibregl.Popup({ offset: 24 }).setHTML(this.#popupHtml(dealer)))
        .addTo(this.#map);

      this.#markersByDealer.set(dealer, marker);
    }
  }

  #createMarkerElement() {
    const pin = document.createElement('div');
    pin.className = 'haendlersuche-map__pin';
    pin.innerHTML = `
      <svg viewBox="0 0 24 32" width="28" height="36" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M12 0C5.373 0 0 5.373 0 12c0 9 12 20 12 20s12-11 12-20c0-6.627-5.373-12-12-12Z" fill="currentColor"/>
        <circle cx="12" cy="12" r="4.5" fill="var(--color-background)"/>
      </svg>
    `;
    return pin;
  }

  /**
   * OpenFreeMap's Liberty style hardcodes every label layer's text-field to
   * `coalesce(name_en, name)`, so place labels render in English regardless
   * of browser/store locale. Swap it to prefer the German name, falling back
   * to the OSM default `name` tag.
   */
  #localizeLabels() {
    if (!this.#map) return;

    for (const layer of this.#map.getStyle().layers) {
      if (layer.type !== 'symbol') continue;

      const textField = this.#map.getLayoutProperty(layer.id, 'text-field');
      // Only rewrite layers using the style's name_en/name place-name
      // expression. Route-shield layers (e.g. highway-shield-non-us) use a
      // ["to-string", ["get", "ref"]] expression for numbers like "A2" —
      // leave those untouched.
      if (!Array.isArray(textField) || !JSON.stringify(textField).includes('name_en')) continue;

      this.#map.setLayoutProperty(layer.id, 'text-field', [
        'case',
        ['has', 'name:nonlatin'],
        ['concat', ['get', 'name:latin'], '\n', ['get', 'name:nonlatin']],
        ['coalesce', ['get', 'name:de'], ['get', 'name']],
      ]);
    }
  }

  /** @param {Dealer} dealer */
  #popupHtml(dealer) {
    return `
      <div class="haendlersuche-map__popup">
        <p class="haendlersuche-map__popup-name">${this.#escapeHtml(dealer.name)}</p>
        <p class="haendlersuche-map__popup-address">${this.#escapeHtml(dealer.address)}</p>
      </div>
    `;
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#map?.remove();
    this.#map = null;
    this.#markersByDealer.clear();
  }

  /** @param {Event} event */
  handleSearchInput(event) {
    const query = /** @type {HTMLInputElement} */ (event.target).value.trim().toLowerCase();

    if (!query) {
      this.#renderSuggestions([]);
      return;
    }

    const matches = this.#dealers
      .filter(
        (dealer) => dealer.name.toLowerCase().includes(query) || dealer.address.toLowerCase().includes(query)
      )
      .slice(0, MAX_SUGGESTIONS);

    this.#renderSuggestions(matches);
  }

  /** @param {KeyboardEvent} event */
  handleSearchKeydown(event) {
    if (!this.#activeSuggestions.length) return;

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      this.#moveSuggestionSelection(1);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      this.#moveSuggestionSelection(-1);
    } else if (event.key === 'Enter') {
      event.preventDefault();
      const dealer =
        this.#activeSuggestions[this.#activeSuggestionIndex] ?? this.#activeSuggestions[0];
      if (dealer) this.#selectDealer(dealer);
    } else if (event.key === 'Escape') {
      this.#renderSuggestions([]);
    }
  }

  /** @param {Event} event */
  handleSuggestionClick(event) {
    // The theme's on:pointerdown dispatch proxies event.target to the
    // resolved element (see component.js) but leaves currentTarget as-is
    // (the delegated document-level listener) — target is what we want here.
    const target = /** @type {HTMLElement} */ (event.target);
    const index = Number(target.dataset.suggestionIndex);
    const dealer = this.#activeSuggestions[index];
    if (dealer) this.#selectDealer(dealer);
  }

  /** @param {Dealer[]} dealers */
  #renderSuggestions(dealers) {
    this.#activeSuggestions = dealers;
    this.#activeSuggestionIndex = -1;

    const list = this.refs.suggestionsList;
    list.textContent = '';
    this.refs.searchInput.setAttribute('aria-expanded', dealers.length > 0 ? 'true' : 'false');
    this.refs.searchInput.setAttribute('aria-activedescendant', '');

    if (!dealers.length) {
      list.hidden = true;
      return;
    }

    dealers.forEach((dealer, index) => {
      const item = document.createElement('li');
      item.id = `haendlersuche-map-suggestion-${index}`;
      item.className = 'haendlersuche-map__suggestion';
      item.dataset.suggestionIndex = String(index);
      item.setAttribute('role', 'option');
      item.setAttribute('aria-selected', 'false');
      item.setAttribute('on:pointerdown', '/handleSuggestionClick');

      const name = document.createElement('span');
      name.className = 'haendlersuche-map__suggestion-name';
      name.textContent = dealer.name;

      const address = document.createElement('span');
      address.className = 'haendlersuche-map__suggestion-address';
      address.textContent = dealer.address;

      item.append(name, address);
      list.append(item);
    });

    list.hidden = false;
  }

  /** @param {number} delta */
  #moveSuggestionSelection(delta) {
    const count = this.#activeSuggestions.length;

    if (this.#activeSuggestionIndex === -1) {
      this.#activeSuggestionIndex = delta > 0 ? 0 : count - 1;
    } else {
      this.#activeSuggestionIndex = (this.#activeSuggestionIndex + delta + count) % count;
    }

    const items = [...this.refs.suggestionsList.children];
    items.forEach((item, index) => {
      item.setAttribute('aria-selected', String(index === this.#activeSuggestionIndex));
    });

    const activeItem = items[this.#activeSuggestionIndex];
    this.refs.searchInput.setAttribute('aria-activedescendant', activeItem?.id ?? '');
  }

  /** @param {Dealer} dealer */
  #selectDealer(dealer) {
    if (this.#map) {
      this.#map.flyTo({ center: [dealer.lng, dealer.lat], zoom: DETAIL_ZOOM, essential: true });

      const marker = this.#markersByDealer.get(dealer);
      marker?.getPopup()?.addTo(this.#map);
    }

    this.refs.searchInput.value = dealer.name;
    this.#renderSuggestions([]);
  }

  /** @param {string} value */
  #escapeHtml(value) {
    const div = document.createElement('div');
    div.textContent = value;
    return div.innerHTML;
  }
}

customElements.define('dealer-map-component', DealerMapComponent);
